import { AxiosResponse } from 'axios';

import { IGetSimpleListResponse } from 'models/http/common';
import { IDataExportResponse } from 'models/http/data-export';
import { IGetHistoryResponse } from 'models/http/history';
import {
  ICreateLocationRequest,
  ICreateLocationResponse,
  IDeleteLocationResponse,
  IDeleteLocationsFilters,
  IGetLocationsFilters,
  IGetLocationSimpleListFilters,
  IGetLocationsResponse,
  IGetSignleLocationResponse,
  ILocationPdfExportRequest,
  IUpdateLocationRequest,
  IUpdateLocationResponse,
} from 'models/http/location';
import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { DataUploadModels, ModelsNames } from 'utils/enums/model-names';

import { prepareFiltersOfSelection, prepareQPFromFilters, serverAxios } from '../../http';
import { HTTP } from './core.http';
import DataUploadBaseHttp from './utils/data-upload.http';

class LocationHttp extends DataUploadBaseHttp implements HTTP<IDeleteLocationsFilters> {
  protected readonly route: DataUploadModels = ModelsNames.Locations;
  public static DataExportRoute = `${ModelsNames.Locations}/data-export`;
  public static PdfExportRoute = `${ModelsNames.Locations}/pdf-export`;

  public async history(id: string, abortController?: AbortController): Promise<IGetHistoryResponse> {
    return serverAxios
      .get(`/${this.route}/${id}/history`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Highest,
      })
      .then((response: AxiosResponse<IGetHistoryResponse>) => response.data);
  }

  public async getSimple(filters?: IGetLocationSimpleListFilters, abortController?: AbortController): Promise<IGetSimpleListResponse> {
    const queryParams = prepareQPFromFilters({ type: filters?.type, facilityTypes: filters?.facilityTypes });

    return serverAxios
      .get(`/${this.route}/simple?${queryParams.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSimpleListResponse>) => {
        return response.data;
      });
  }

  public async get(filters?: IGetLocationsFilters, abortController?: AbortController): Promise<IGetLocationsResponse> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`/${this.route}?${queryParams.toString()}`, {
        data: filters,
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetLocationsResponse>) => {
        return response.data;
      });
  }

  public async getById(id: string, abortController?: AbortController): Promise<IGetSignleLocationResponse> {
    return serverAxios
      .get(`/${this.route}/single/${id}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSignleLocationResponse>) => {
        return response.data;
      });
  }

  public async create(data: ICreateLocationRequest): Promise<ICreateLocationResponse> {
    return serverAxios
      .post(`/${this.route}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<ICreateLocationResponse>) => {
        return response.data;
      });
  }

  public async update(id: number, data: IUpdateLocationRequest): Promise<IUpdateLocationResponse> {
    return serverAxios
      .put(`/${this.route}/${id}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IUpdateLocationResponse>) => {
        return response.data;
      });
  }

  public async delete(ids: number[]): Promise<IDeleteLocationResponse>;
  public async delete(filters: IDeleteLocationsFilters): Promise<IDeleteLocationResponse>;
  public async delete(filters: number[] | IDeleteLocationsFilters): Promise<IDeleteLocationResponse> {
    return serverAxios
      .delete(`/${this.route}`, {
        data: prepareFiltersOfSelection(filters),
      })
      .then((response: AxiosResponse<IDeleteLocationResponse>) => {
        return response.data;
      });
  }

  public async pdfExport(data: ILocationPdfExportRequest): Promise<IDataExportResponse> {
    return serverAxios
      .post(`/${LocationHttp.PdfExportRoute}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IDataExportResponse>) => response.data);
  }
}

export default LocationHttp;
